import React, { useMemo, useState } from "react";
import CustomTable, { Column } from "../../../../components/CustomTable";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import StaticModal from "../../../../components/staticModel";
import {
  ProjectDetailResponse,
  ProjectUser,
} from "../../../../model/projectModel";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import TeamForms from "./forms/TeamForm";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import BackButton from "../../../../components/BackButton";
const TeamMember: React.FC<{ details: ProjectDetailResponse | null }> = ({
  details,
}) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editFormValue, setEditFormValue] = useState<ProjectUser | null>(null);
  const [formType, setFormType] = useState<string>("create");
  const [memberFilterValue, setMemberFilterValue] = useState<string>("");
  const [statusValue, setStatusValue] = useState<string>("active");

  const location = useLocation();
  const hasAutoOpenedModal = useRef(false);

  const teamMembers = useMemo(() => {
    return details?.users
      ? details.users
          .filter((member) => {
            const matchesName = member["Employee Name"]
              .toLowerCase()
              .includes(memberFilterValue.toLowerCase());
            const endMoment = moment(member.end_ts);
            // FIX: Check if end date is null OR is today/in the future
            const isMemberActive =
              member.end_ts === null ||
              (endMoment.isValid() && endMoment.isSameOrAfter(moment(), "day"));

            const matchesStatus =
              statusValue === "active"
                ? isMemberActive
                : statusValue === "inactive"
                  ? !isMemberActive
                  : true;

            return matchesName && matchesStatus;
          })
          .map((item, index) => {
            return {
              ...item,
              id: index + 1,
              start: moment(item.start_ts).isValid()
                ? moment(item.start_ts).format("DD/MM/YYYY")
                : "",
              end: moment(item.end_ts).isValid()
                ? moment(item.end_ts).format("DD/MM/YYYY")
                : "",
              user_type:
                item.is_approver === "No" && item.isaltmanager === "No"
                  ? "User"
                  : item.is_approver === "Yes" &&
                      item["Employee ldap"] === details.manager
                    ? "Approver & Manager"
                    : item.is_approver === "Yes"
                      ? "Approver"
                      : "User & Approver",
            };
          })
      : null;
  }, [details, memberFilterValue, statusValue]);
  useEffect(() => {
    const { openUserModal, targetEmployeeLdap } = location.state || {};

    if (
      openUserModal &&
      teamMembers &&
      teamMembers.length > 0 &&
      !hasAutoOpenedModal.current
    ) {
      hasAutoOpenedModal.current = true;

      let targetMember = null;
      if (targetEmployeeLdap) {
        targetMember = teamMembers.find(
          (m) =>
            m["Employee ldap"] === targetEmployeeLdap ||
            m["Employee Name"] === targetEmployeeLdap,
        );
      }

      if (targetMember) {
        handleEdit(targetMember.id);
      }

      // Consume the intent: strip it from location.state so a remount
      // (e.g. from the isLoading unmount/remount cycle) can't reopen it.
      navigate(location.pathname, {
        replace: true,
        state: {
          ...location.state,
          openUserModal: false,
          targetEmployeeLdap: undefined,
        },
      });
    }
  }, [teamMembers, location.state]);
  const columns: Column<ProjectUser>[] = [
    {
      header: "Employee Name",
      key: "Employee Name",
      enableLink: true,
      path: "/",
    },
    {
      header: "Emp ID",
      key: "Employee_team_member_id",
      enableLink: false,
      path: "",
    },
    { header: "Billable", key: "is_billable", enableLink: false, path: "" },
    { header: "Location", key: "location", enableLink: false, path: "" },
    { header: "Start Date", key: "start", enableLink: false, path: "" },
    { header: "End Date", key: "end", enableLink: false, path: "" },
    {
      header: "Shift Details",
      key: "shift_details",
      enableLink: false,
      path: "",
    },
    { header: "User Type", key: "user_type", enableLink: false, path: "" },
    {
      header: "Approver Name [LDAP User Id]",
      key: "Approver Name",
      enableLink: false,
      path: "",
    },
  ];

  const handleMemberChange = (selectedOption: string | null) => {
    setMemberFilterValue(selectedOption ?? "");
  };

  const handleTableRefresh = () => {
    setStatusValue("active");
    setMemberFilterValue("");
  };

  // const openModal = () => setIsModalOpen(true);
  // const closeModal = () => setIsModalOpen(false);
  const openModal = () => {
    console.log("[openModal] called");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    console.log("[closeModal] called");
    // setEditFormValue(null); // if you have this, otherwise omit
    setIsModalOpen(false);
  };
  const handleEdit = (id: number) => {
    const editValue = teamMembers?.find((item) => item.id === id) ?? null;
    setEditFormValue(editValue);
    setFormType("update");
    openModal();
  };

  const handleNavigation = (path: string | undefined, stateId: number) => {
    navigate(path ?? "", { state: { id: stateId } });
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-3">
        <BackButton
          fallbackPath="/"
          className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2.5 text-center"
        />

        <button
          type="button"
          onClick={() => {
            setFormType("create");
            openModal();
          }}
          className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2.5 text-center"
        >
          Add Team Member
        </button>
      </div>
      {/* <button onClick={handleBackTousersearchList}>? Back</button> */}
      <div className="max-w-4xl gap-x-6 gap-y-8 grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
        <div>
          <label
            htmlFor="member-filter"
            className="block text-sm font-medium leading-6 text-gray-900 dark:text-white"
          >
            Search a member
          </label>
          <div className="mt-2">
            <select
              id="member-filter"
              value={memberFilterValue}
              onChange={(e) => handleMemberChange(e.target.value)}
              className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
            >
              <option value="">All</option>
              {details?.users.map((item) => (
                <option key={item["Employee id"]} value={item["Employee Name"]}>
                  {item["Employee Name"]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label
            htmlFor="status"
            className="block text-sm font-medium leading-6 text-gray-900 dark:text-white"
          >
            Status
          </label>
          <div className="mt-2">
            <select
              id="status"
              value={statusValue}
              onChange={(e) => setStatusValue(e.target.value)}
              className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
            >
              <option value="">Select</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="flex space-x-4">
          <button
            onClick={handleTableRefresh}
            className="flex items-center px-4 py-2 mt-9 text-sm bg-blue-700 hover:bg-blue-800 text-white rounded-lg focus:outline-none"
          >
            <ArrowPathIcon className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>
      <CustomTable
        idKeyName="id"
        showFilter={false}
        columns={columns}
        data={teamMembers ?? []}
        modelForm={true}
        handleModel={handleEdit}
        handleNavigation={handleNavigation}
      />
      <StaticModal
        headerName="Team Member"
        isOpen={isModalOpen}
        onClose={closeModal}
      >
        <TeamForms
          formType={formType}
          userValue={editFormValue}
          closeModals={closeModal}
        />
      </StaticModal>
    </>
  );
};

export default TeamMember;
